import axios from 'axios';
import {
    CosmoLexClient,
    CosmoLexCredsStatus,
    CosmoLexExistingTransactions,
    CosmoLexMatterSummary,
    CosmoLexNewTransactionInfo,
    CosmoLexOpenResult,
    CosmoLexOpenSectionName,
    CosmoLexTransactionLine,
    CosmoLexTransactionUpdate,
} from './cosmoLexTypes';

// All CosmoLex data flows through the RPlusData proxy — the LCS API is CORS-restricted
// and its ApiKey/user token live server-side only. Auth follows the rwapi convention:
// SessionID as a query param, validated by RPlusData's checkSession middleware.

const RWAPI = process.env.REACT_APP_RWAPI_ENDPOINT;

function baseUrl(prov: string, firmId: number, sid: string, path: string): string {
    return `/${RWAPI}/api/${prov}/${firmId}/cosmolex${path}?SessionID=${encodeURIComponent(sid)}`;
}

export function extractCosmoLexError(error: unknown): string {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { error?: string; message?: string } | undefined;
        return data?.error || data?.message || error.message;
    }
    return error instanceof Error ? error.message : 'Unexpected error';
}

/** Which credential level the proxy can resolve for this user (user / firm / none). */
export async function fetchCredsStatus(prov: string, firmId: number, sid: string): Promise<CosmoLexCredsStatus> {
    const response = await axios.get(baseUrl(prov, firmId, sid, '/status'));
    return response.data.result.credentials as CosmoLexCredsStatus;
}

/**
 * Modal open in one round trip: credential status plus the requested sections for a
 * remembered matter, fetched in parallel by the proxy. With no matterId only the
 * credential status comes back (same cost as fetchCredsStatus).
 */
export async function fetchCosmoLexOpen(
    prov: string,
    firmId: number,
    sid: string,
    matterId: string,
    include: CosmoLexOpenSectionName[]
): Promise<CosmoLexOpenResult> {
    const response = await axios.get(
        `${baseUrl(prov, firmId, sid, '/open')}&matterId=${encodeURIComponent(matterId || '')}&include=${include.join(',')}`
    );
    return response.data.result as CosmoLexOpenResult;
}

/** Search CosmoLex matters by text (matches client, matter name, or file number upstream). */
export async function searchCosmoLexMatters(
    prov: string,
    firmId: number,
    sid: string,
    search: string
): Promise<CosmoLexMatterSummary[]> {
    const response = await axios.get(
        `${baseUrl(prov, firmId, sid, '/matters')}&search=${encodeURIComponent(search)}`
    );
    return response.data.result as CosmoLexMatterSummary[];
}

/** Trust banks + method lists + chart-of-account defaults for the selected matter. */
export async function fetchNewTransactionInfo(
    prov: string,
    firmId: number,
    sid: string,
    matterId: string
): Promise<CosmoLexNewTransactionInfo> {
    const response = await axios.get(
        `${baseUrl(prov, firmId, sid, '/transactions/new-info')}&matterId=${encodeURIComponent(matterId)}`
    );
    return response.data.result as CosmoLexNewTransactionInfo;
}

/** Existing trust transactions on the matter (first 100, newest first) — read-only preview. */
export async function fetchExistingTransactions(
    prov: string,
    firmId: number,
    sid: string,
    matterId: string
): Promise<CosmoLexExistingTransactions> {
    const response = await axios.get(
        `${baseUrl(prov, firmId, sid, '/transactions/existing')}&matterId=${encodeURIComponent(matterId)}`
    );
    return response.data.result as CosmoLexExistingTransactions;
}

/**
 * Push Realti's values (any subset of amount/name/description) onto a previously
 * exported transaction. The proxy only performs the edit while the transaction
 * is still in "Entered" status in CosmoLex — anything else comes back as an
 * error explaining why.
 */
export async function updateCosmoLexTransaction(
    prov: string,
    firmId: number,
    sid: string,
    transactionId: string,
    update: CosmoLexTransactionUpdate
): Promise<void> {
    await axios.put(
        baseUrl(prov, firmId, sid, `/transactions/${encodeURIComponent(transactionId)}`),
        update
    );
}

/** One matter by id — used to reopen the export modal on the file's remembered matter. */
export async function fetchCosmoLexMatter(
    prov: string,
    firmId: number,
    sid: string,
    matterId: string
): Promise<CosmoLexMatterSummary> {
    const response = await axios.get(baseUrl(prov, firmId, sid, `/matters/${encodeURIComponent(matterId)}`));
    return response.data.result as CosmoLexMatterSummary;
}

/** Post one trust ledger line into CosmoLex. Returns the created transaction id. */
export async function createCosmoLexTransaction(
    prov: string,
    firmId: number,
    sid: string,
    line: CosmoLexTransactionLine
): Promise<string | null> {
    const response = await axios.post(baseUrl(prov, firmId, sid, '/transactions'), line);
    return (response.data.result?.id as string) ?? null;
}

/** Full client record for the import preview. */
export async function fetchCosmoLexClient(
    prov: string,
    firmId: number,
    sid: string,
    clientId: string
): Promise<CosmoLexClient> {
    const response = await axios.get(baseUrl(prov, firmId, sid, `/clients/${encodeURIComponent(clientId)}`));
    return response.data.result as CosmoLexClient;
}

export async function createCosmoLexExpense(
    prov: string,
    firmId: number,
    sid: string,
    body: object,
): Promise<any | null> {
    const response = await axios.post(baseUrl(prov, firmId, sid, '/expense'), body);
    return (response.data.result) ?? null;
}

/**
 * A proxy build that still swallows upstream errors answers 200 with { ok: false } —
 * treat that as a failure so the row is never marked updated on a failed PUT.
 */
function assertOk(response: { data?: { ok?: boolean; error?: string } }, fallback: string): void {
    if (response.data?.ok === false) {
        throw new Error(response.data.error || fallback);
    }
}

export async function updateCosmoLexExpense(
    prov: string,
    firmId: number,
    sid: string,
    id: string,
    body: object,
): Promise<void> {
    const response = await axios.put(
        baseUrl(prov, firmId, sid, `/expense/${encodeURIComponent(id)}`),
        body
    );
    assertOk(response, 'CosmoLex did not update the expense.');
}

export async function getCosmoLexExpenses(
    prov: string,
    firmId: number,
    sid: string,
    matterId: string,
): Promise<any | null> {
    const response = await axios.get(baseUrl(prov, firmId, sid, `/expenses/${encodeURIComponent(matterId)}`));
    return (response.data.result) ?? null;
}

export async function createCosmoLexTimecard(
    prov: string,
    firmId: number,
    sid: string,
    body: object,
): Promise<any | null> {
    const response = await axios.post(baseUrl(prov, firmId, sid, '/timecard'), body);
    return (response.data.result) ?? null;
}

export async function updateCosmoLexTimecard(
    prov: string,
    firmId: number,
    sid: string,
    id: string,
    body: object,
): Promise<void> {
    const response = await axios.put(
        baseUrl(prov, firmId, sid, `/timecard/${encodeURIComponent(id)}`),
        body
    );
    assertOk(response, 'CosmoLex did not update the time entry.');
}